import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  MessageSquare,
  Type,
  Paperclip,
  PenSquare,
  MessageSquareText,
  Brain,
  Tags,
  ListChecks,
  MessageSquarePlus,
  BookOpenCheck,
  GitBranch,
  SlidersHorizontal,
  Clock,
  UserPlus,
  BookOpen,
  Webhook,
  Layers,
  List,
  ExternalLink,
  LayoutGrid,
  ArrowRightLeft,
  Braces,
  LayoutTemplate,
  StopCircle,
  ChevronDown,
  Plus,
  Sparkles,
  X,
} from "lucide-react";

export const NODE_CATALOG = [
  {
    title: "Core",
    items: [
      { type: "trigger", label: "Trigger", icon: MessageSquare, color: "purple" },
      { type: "textMessage", label: "Text Message", icon: Type, color: "blue" },
      { type: "sendMedia", label: "Send Media", icon: Paperclip, color: "amber" },
      { type: "setVariable", label: "Set Variable", icon: PenSquare, color: "emerald" },
      { type: "captureText", label: "Ask question (text)", icon: MessageSquareText, color: "teal" },
    ],
  },
  {
    title: "AI",
    items: [
      { type: "aiAskQuestion", label: "AI Ask Question", icon: Brain, color: "purple" },
      { type: "aiIntentClassifier", label: "AI Intent Classifier", icon: Tags, color: "purple" },
      { type: "aiEntityExtractor", label: "AI Entity Extractor", icon: ListChecks, color: "purple" },
      { type: "aiResponseGenerator", label: "AI Response Generator", icon: MessageSquarePlus, color: "purple" },
      { type: "aiKnowledgeBaseAnswer", label: "AI Smart Assistant", icon: BookOpenCheck, color: "purple" },
    ],
  },
  {
    title: "Logic & Actions",
    items: [
      { type: "condition", label: "Condition", icon: GitBranch, color: "blue" },
      { type: "controller", label: "Controller", icon: SlidersHorizontal, color: "orange" },
      { type: "delay", label: "Delay", icon: Clock, color: "blue" },
      { type: "assignAgent", label: "Assign Agent", icon: UserPlus, color: "blue" },
      { type: "knowledgeBase", label: "Knowledge Base", icon: BookOpen, color: "blue" },
      { type: "webhook", label: "Webhook", icon: Webhook, color: "orange" },
      { type: "codeMap", label: "Code Transform", icon: Braces, color: "blue" },
      { type: "transferToFlow", label: "Transfer to Flow", icon: ArrowRightLeft, color: "indigo" },
      { type: "end", label: "End Flow", icon: StopCircle, color: "red" },
    ],
  },
  {
    title: "Interactive",
    items: [
      { type: "preApprovedTemplate", label: "Pre-approved Template", icon: LayoutTemplate, color: "blue" },
      { type: "interactiveButtons", label: "Body + Buttons", icon: MessageSquare, color: "teal" },
      { type: "interactiveList", label: "Body + Lists", icon: List, color: "teal" },
      { type: "interactiveUrl", label: "Body + URL", icon: ExternalLink, color: "teal" },
      { type: "interactiveCarouselUrl", label: "Carousel URL", icon: LayoutGrid, color: "teal" },
      { type: "interactiveCarouselButton", label: "Carousel Button", icon: LayoutGrid, color: "teal" },
    ],
  },
];

const COLOR_MAP = {
  purple: { icon: "text-purple-600", hover: "hover:bg-purple-50 hover:border-purple-200" },
  blue: { icon: "text-blue-600", hover: "hover:bg-blue-50 hover:border-blue-200" },
  teal: { icon: "text-teal-600", hover: "hover:bg-teal-50 hover:border-teal-200" },
  amber: { icon: "text-amber-600", hover: "hover:bg-amber-50 hover:border-amber-200" },
  emerald: { icon: "text-emerald-600", hover: "hover:bg-emerald-50 hover:border-emerald-200" },
  orange: { icon: "text-orange-600", hover: "hover:bg-orange-50 hover:border-orange-200" },
  indigo: { icon: "text-indigo-600", hover: "hover:bg-indigo-50 hover:border-indigo-200" },
  red: { icon: "text-red-600", hover: "hover:bg-red-50 hover:border-red-200" },
};

function PickerItem({ item, onSelect }) {
  const Icon = item.icon;
  const colors = COLOR_MAP[item.color] || COLOR_MAP.blue;
  return (
    <button
      type="button"
      onClick={() => onSelect(item.type)}
      className={`w-full flex items-center gap-2.5 px-2.5 py-2 text-left rounded-lg border border-transparent transition-colors ${colors.hover}`}
    >
      <Icon size={15} className={`flex-shrink-0 ${colors.icon}`} />
      <span className="text-sm font-medium text-gray-800">{item.label}</span>
    </button>
  );
}

export function AddNodePickerPanel({ title, subtitle, onSelect, onClose, className = "" }) {
  const [expandedInteractive, setExpandedInteractive] = useState(true);

  return (
    <div
      className={`bg-white border border-gray-200 rounded-2xl shadow-xl overflow-hidden flex flex-col ${className}`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-indigo-50 to-white flex items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-bold text-gray-900">{title}</h3>
          {subtitle && <p className="text-[11px] text-gray-500 mt-0.5">{subtitle}</p>}
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-gray-100 text-gray-400"
          >
            <X size={14} />
          </button>
        )}
      </div>
      <div className="overflow-y-auto max-h-[min(420px,60vh)] p-2">
        {NODE_CATALOG.filter((s) => s.title !== "Interactive").map((section) => (
          <div key={section.title} className="mb-2">
            <p className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-gray-400">
              {section.title}
            </p>
            <div className="space-y-0.5">
              {section.items.map((item) => (
                <PickerItem key={item.type} item={item} onSelect={onSelect} />
              ))}
            </div>
          </div>
        ))}

        <div className="mb-1">
          <button
            type="button"
            onClick={() => setExpandedInteractive((v) => !v)}
            className="w-full flex items-center justify-between px-2 py-1.5 rounded-lg hover:bg-teal-50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Layers size={14} className="text-teal-600" />
              <span className="text-[10px] font-bold uppercase tracking-wider text-teal-700">
                Interactive
              </span>
            </div>
            <ChevronDown
              size={14}
              className={`text-gray-400 transition-transform ${expandedInteractive ? "rotate-180" : ""}`}
            />
          </button>
          {expandedInteractive && (
            <div className="ml-2 pl-2 border-l-2 border-teal-100 space-y-0.5 mt-0.5">
              {NODE_CATALOG.find((s) => s.title === "Interactive")?.items.map((item) => (
                <PickerItem key={item.type} item={item} onSelect={onSelect} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function useClickOutside(ref, onClose, isOpen) {
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    const handleEscape = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [ref, onClose, isOpen]);
}

/** Toolbar dropdown — add node at cursor / center */
export function FlowAddNodeMenu({ onSelectNode, onOpenAI }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useClickOutside(ref, () => setOpen(false), open);

  const handleSelect = (type) => {
    onSelectNode(type);
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-semibold rounded-xl border transition-colors ${
          open
            ? "bg-indigo-50 border-indigo-300 text-indigo-800"
            : "text-indigo-700 border-indigo-200 bg-indigo-50/60 hover:bg-indigo-50"
        }`}
      >
        <Plus size={16} />
        Add node
        <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-72 z-50">
          <AddNodePickerPanel
            title="Add node to canvas"
            subtitle="Placed at cursor or center"
            onSelect={handleSelect}
            onClose={() => setOpen(false)}
          />
          {onOpenAI && (
            <button
              type="button"
              onClick={() => {
                onOpenAI();
                setOpen(false);
              }}
              className="mt-2 w-full flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-semibold text-purple-700 bg-purple-50 border border-purple-200 rounded-xl hover:bg-purple-100 transition-colors"
            >
              <Sparkles size={15} />
              Generate with AI
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/** Floating picker anchored above a node (+ button) */
export function NodeAddNodePicker({ open, anchor, onSelect, onClose }) {
  const ref = useRef(null);
  useClickOutside(ref, onClose, open);

  if (!open || !anchor) return null;

  const handleSelect = (type) => {
    onSelect(type);
    onClose();
  };

  return createPortal(
    <div
      ref={ref}
      className="fixed z-[1000] w-72"
      style={{
        left: anchor.x,
        top: anchor.y,
        transform: "translate(-50%, calc(-100% - 10px))",
      }}
    >
      <AddNodePickerPanel
        title="Add next node"
        subtitle="Connected from this step"
        onSelect={handleSelect}
        onClose={onClose}
      />
    </div>,
    document.body
  );
}
